import React, { useMemo } from 'react';
import { Question } from '../../../types';
import { generateSupportCoverageReport, FamilyCoverageStatus } from '../../../support/supportCoverageReport';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { CheckCircle2, AlertTriangle, AlertOctagon, PieChart, HelpCircle, Eye, FileQuestion, LayoutList, XCircle } from 'lucide-react';
interface TaxonomyCoverageProps {
  questions: Question[];
}
export function TaxonomyCoverage({
  questions
}: TaxonomyCoverageProps) {
  const report = useMemo(() => generateSupportCoverageReport(questions), [questions]);
  const {
    summary,
    byModule,
    familyStatuses
  } = report;
  const getStatusColor = (status: FamilyCoverageStatus['status']) => {
    switch (status) {
      case 'complete':
        return 'text-emerald-500';
      case 'partial':
        return 'text-amber-500';
      case 'missing_support':
        return 'text-rose-500';
      case 'missing_questions':
        return 'text-blue-500';
      case 'empty':
        return 'text-slate-300';
    }
  };
  const getStatusIcon = (status: FamilyCoverageStatus['status']) => {
    switch (status) {
      case 'complete':
        return CheckCircle2;
      case 'partial':
        return AlertTriangle;
      case 'missing_support':
        return AlertOctagon;
      case 'missing_questions':
        return FileQuestion;
      case 'empty':
        return XCircle;
    }
  };
  return <div className="space-y-8" data-id="element-1487">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-1488">
        <SummaryCard title="Complete" count={summary.complete} total={report.totalFamilies} icon={CheckCircle2} color="emerald" data-id="element-1489" />

        <SummaryCard title="Partial" count={summary.partial} total={report.totalFamilies} icon={AlertTriangle} color="amber" data-id="element-1490" />

        <SummaryCard title="No Support" count={summary.missingSupport} total={report.totalFamilies} icon={AlertOctagon} color="rose" data-id="element-1491" />

        <SummaryCard title="No Questions" count={summary.missingQuestions} total={report.totalFamilies} icon={FileQuestion} color="blue" data-id="element-1492" />

        <SummaryCard title="Empty" count={summary.empty} total={report.totalFamilies} icon={XCircle} color="slate" data-id="element-1493" />
      </div>

      {/* Module Breakdown */}
      <Card className="p-6 border-slate-200" data-id="element-1494">
        <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-1495">
          <LayoutList className="w-5 h-5 text-indigo-600" data-id="element-1496" />
          Module Breakdown
        </h3>
        <div className="overflow-x-auto" data-id="element-1497">
          <table className="w-full text-sm text-left" data-id="element-1498">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-1499">
              <tr data-id="element-1500">
                <th className="px-4 py-3 font-bold" data-id="element-1501">Module</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1502">Total</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1503">Supported</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1504">Missing</th>
                <th className="px-4 py-3 font-bold" data-id="element-1505">Coverage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1506">
              {Object.entries(byModule).map(([mod, stats]) => {
              const percentage = Math.round(stats.supported / stats.total * 100);
              return <tr key={mod} className="hover:bg-slate-50/50" data-id="element-1507">
                    <td className="px-4 py-3 font-medium text-slate-900" data-id="element-1508">
                      {mod}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600" data-id="element-1509">
                      {stats.total}
                    </td>
                    <td className="px-4 py-3 text-center text-emerald-600 font-bold" data-id="element-1510">
                      {stats.supported}
                    </td>
                    <td className="px-4 py-3 text-center text-rose-600 font-bold" data-id="element-1511">
                      {stats.missing.length}
                    </td>
                    <td className="px-4 py-3" data-id="element-1512">
                      <div className="flex items-center gap-3" data-id="element-1513">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-1514">
                          <div className={`h-full rounded-full ${percentage === 100 ? 'bg-emerald-500' : percentage >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{
                        width: `${percentage}%`
                      }} data-id="element-1515" />
                        </div>
                        <span className="text-xs font-bold text-slate-500 w-8 text-right" data-id="element-1516">
                          {percentage}%
                        </span>
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Critical Issues List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1517">
        {/* Missing Support */}
        <Card className="p-6 border-rose-200 bg-rose-50/10" data-id="element-1518">
          <h3 className="font-bold text-rose-900 mb-4 flex items-center gap-2" data-id="element-1519">
            <AlertOctagon className="w-5 h-5 text-rose-600" data-id="element-1520" />
            Missing Support ({report.questionsWithoutSupport.length})
          </h3>
          <p className="text-xs text-rose-700 mb-4" data-id="element-1521">
            These families have runnable questions but NO support content
            defined.
          </p>
          <div className="space-y-2 max-h-60 overflow-y-auto pr-2" data-id="element-1522">
            {report.questionsWithoutSupport.length === 0 ? <div className="text-sm text-emerald-600 font-medium flex items-center gap-2" data-id="element-1523">
                <CheckCircle2 className="w-4 h-4" data-id="element-1524" /> All active families have
                support!
              </div> : report.questionsWithoutSupport.map(id => <div key={id} className="flex items-center justify-between p-2 bg-white rounded border border-rose-100 text-sm" data-id="element-1525">
                  <span className="font-mono font-bold text-slate-700" data-id="element-1526">
                    {id}
                  </span>
                  <Badge variant="neutral" size="sm" data-id="element-1527">
                    Needs Entry
                  </Badge>
                </div>)}
          </div>
        </Card>

        {/* Missing Questions */}
        <Card className="p-6 border-blue-200 bg-blue-50/10" data-id="element-1528">
          <h3 className="font-bold text-blue-900 mb-4 flex items-center gap-2" data-id="element-1529">
            <FileQuestion className="w-5 h-5 text-blue-600" data-id="element-1530" />
            Unused Support ({report.supportWithoutQuestions.length})
          </h3>
          <p className="text-xs text-blue-700 mb-4" data-id="element-1531">
            Support content exists, but no questions use this family ID yet.
          </p>
          <div className="space-y-2 max-h-60 overflow-y-auto pr-2" data-id="element-1532">
            {report.supportWithoutQuestions.length === 0 ? <div className="text-sm text-slate-500 italic" data-id="element-1533">
                No orphaned support entries.
              </div> : report.supportWithoutQuestions.map(id => <div key={id} className="flex items-center justify-between p-2 bg-white rounded border border-blue-100 text-sm" data-id="element-1534">
                  <span className="font-mono font-bold text-slate-700" data-id="element-1535">
                    {id}
                  </span>
                  <Badge variant="neutral" size="sm" data-id="element-1536">
                    Needs Questions
                  </Badge>
                </div>)}
          </div>
        </Card>
      </div>

      {/* Detailed Family Status Table */}
      <Card className="p-6 border-slate-200" data-id="element-1537">
        <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-1538">
          <PieChart className="w-5 h-5 text-indigo-600" data-id="element-1539" />
          Detailed Family Status
        </h3>
        <div className="overflow-x-auto" data-id="element-1540">
          <table className="w-full text-sm text-left" data-id="element-1541">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-1542">
              <tr data-id="element-1543">
                <th className="px-4 py-3 font-bold" data-id="element-1544">Family ID</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1545">Status</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1546">Questions</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1547">Hints</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1548">Visual</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1549">Explain</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1550">
              {familyStatuses.map(status => {
              const StatusIcon = getStatusIcon(status.status);
              const statusClass = getStatusColor(status.status);
              return <tr key={status.familyId} className="hover:bg-slate-50/50" data-id="element-1551">
                    <td className="px-4 py-3" data-id="element-1552">
                      <div className="font-mono font-bold text-slate-700 text-xs" data-id="element-1553">
                        {status.familyId}
                      </div>
                      <div className="text-xs text-slate-500 truncate max-w-[200px]" data-id="element-1554">
                        {status.description}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1555">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${statusClass.replace('text-', 'bg-').replace('500', '50')} ${statusClass} border-${statusClass.split('-')[1]}-200`} data-id="element-1556">
                        <StatusIcon className="w-3.5 h-3.5" data-id="element-1557" />
                        {status.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1558">
                      {status.questionCount > 0 ? <span className="font-bold text-slate-700" data-id="element-1559">
                          {status.questionCount}
                        </span> : <span className="text-slate-300" data-id="element-1560">-</span>}
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1561">
                      {status.hasHints ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" data-id="element-1562" /> : status.requiresHints ? <span className="text-rose-500 font-bold text-xs" data-id="element-1563">
                          REQ
                        </span> : <span className="text-slate-300" data-id="element-1564">-</span>}
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1565">
                      {status.hasVisual ? <Eye className="w-4 h-4 text-emerald-500 mx-auto" data-id="element-1566" /> : status.requiresVisual ? <span className="text-rose-500 font-bold text-xs" data-id="element-1567">
                          REQ
                        </span> : <span className="text-slate-300" data-id="element-1568">-</span>}
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1569">
                      {status.hasExplanation ? <HelpCircle className="w-4 h-4 text-emerald-500 mx-auto" data-id="element-1570" /> : <span className="text-slate-300" data-id="element-1571">-</span>}
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
}
function SummaryCard({
  title,
  count,
  total,
  icon: Icon,
  color
}: {
  title: string;
  count: number;
  total: number;
  icon: React.ElementType;
  color: 'emerald' | 'amber' | 'rose' | 'blue' | 'slate';
}) {
  const colorClasses: Record<string, string> = {
    emerald: 'text-emerald-600 bg-emerald-50',
    amber: 'text-amber-600 bg-amber-50',
    rose: 'text-rose-600 bg-rose-50',
    blue: 'text-blue-600 bg-blue-50',
    slate: 'text-slate-600 bg-slate-50'
  };
  return <Card className="p-4 flex items-center justify-between border-slate-200" data-id="element-1572">
      <div data-id="element-1573">
        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-1574">
          {title}
        </div>
        <div className="text-2xl font-bold text-slate-900" data-id="element-1575">{count}</div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-1576">
          {Math.round(count / total * 100)}% of total
        </div>
      </div>
      <div className={`p-3 rounded-full ${colorClasses[color]}`} data-id="element-1577">
        <Icon className="w-6 h-6" data-id="element-1578" />
      </div>
    </Card>;
}